import { CurrencyPipe } from '@angular/common';
import type { PipeTransform } from '@angular/core';
import { LOCALE_ID, Pipe, inject } from '@angular/core';
import { isDefined, toNumber } from '@freelancer/utils';
import { currencySignMap } from './currency-sign-map';

export interface AmountRange {
  minimum: number;
  maximum?: number;
}

export interface CurrencyFormatOptions {
  disableCode?: boolean;
  negativeParenthesis?: boolean;
  withPrecision?: boolean;
  showSignBeforeMaximum?: boolean;
  // Whether to return amounts in compact format (e.g. 1k, 1.5k, 1M)
  compact?: boolean;
}
/**
 * This pipe provides a unified way to display amount and currency
 * so that it's consistent throughout the freelancer codebase.
 * So far we only have one format `{currency.sign}{amount} {currency.code}`
 * but it's possible to have two formats i.e. `long`/`short`,
 * depending on the product decision. This pipe can also display minimum
 * and maximum amount in range with currency.
 *
 * Note: For single amounts the accounting standards show the negative amounts as `(absAmount)`,
 *  where `absAmount` is absolute amount
 *
 * Example for single amount =
 *  - controller
 *  ```
      this.amount = 130;
      this.currency: Currency = {code: AUD, sign: $, ...};
    ```
 *  - template
 *  ```
      <p> Amount to pay: {{ amount | flCurrency:currency }} </p>
    ```
 *  - output
 *  ```
      Amount to pay: $130 AUD
    ```
 *
 * Example for range =
 *  - controller
 *  ```
      amountRange = {
        minimum: 1300,
        maximum: 2000,
      };
      this.currency: Currency = {code: AUD, sign: $, ...};
    ```
 *  - template
 *  ```
      <p> Project Budget: {{ amountRange | flCurrency: currency.code }} </p>
    ```
 *  - output
 *  ```
      Project Budget: $1,300.00 - 2,000.00 AUD
    ```
 */
@Pipe({ name: 'flCurrency', standalone: true })
// We only called it FreelancerCurrencyPipe because CurrencyPipe already existed
export class FreelancerCurrencyPipe implements PipeTransform {
  private locale = inject(LOCALE_ID);

  readonly NO_FRACTION_FORMAT = '1.0-0';

  transform(
    amount: string | number | AmountRange | null | undefined,
    code: string | null | undefined,
    formatOptionParams?: CurrencyFormatOptions,
  ): string | null {
    const formatOptions: CurrencyFormatOptions = {
      negativeParenthesis: false,
      withPrecision: true,
      compact: false,
      ...formatOptionParams,
    };

    const nonStringAmount =
      typeof amount === 'string' ? toNumber(amount) : amount;
    if (!isDefined(nonStringAmount) || !isDefined(code)) {
      // safety guard for nonexistent values when template is loading
      return null;
    }

    if (this.isTimeBasedCurrency(code)) {
      // TODO: T99032 split this logic into its own pipe
      return typeof nonStringAmount === 'number'
        ? this.displaySingleTimeAmount(nonStringAmount)
        : this.displayRangeTimeAmount(nonStringAmount);
    }

    const currencyPipe = new CurrencyPipe(this.locale);
    return typeof nonStringAmount === 'number'
      ? this.displaySingleAmount(
          nonStringAmount,
          code,
          currencyPipe,
          formatOptions,
        )
      : this.displayRangeAmount(
          nonStringAmount,
          code,
          currencyPipe,
          formatOptions,
        );
  }

  displaySingleAmount(
    amount: number,
    code: string,
    currencyPipe: CurrencyPipe,
    formatOptions: CurrencyFormatOptions,
  ): string | null {
    const absAmount = Math.abs(amount);
    const parentAmount = formatOptions.compact
      ? this.formatCompactAmount(absAmount, formatOptions)
      : currencyPipe.transform(
          absAmount,
          code,
          '',
          // Trying to reduce it to a whole number.
          // Leaving it undefined will allow the currency pipe
          // to format the number to the proper amount of decimal places
          // based on the ISO 4217 standard.
          this.getDigitsFormat(formatOptions.withPrecision, absAmount),
        ) || absAmount.toString();

    const formatted = `${this.getSignForCurrency(code)}${parentAmount} ${
      formatOptions.disableCode ? '' : code
    }`;
    return amount >= 0
      ? formatted
      : formatOptions.negativeParenthesis
        ? `(${formatted})`
        : `-${formatted}`;
  }

  displayRangeAmount(
    amount: AmountRange,
    code: string,
    currencyPipe: CurrencyPipe,
    formatOptions: CurrencyFormatOptions,
  ): string | null {
    if (!isDefined(amount.minimum)) {
      return null;
    }

    /**
     * Only format to whole number if both numbers
     * can be reduced into a whole number.
     */
    const decimalRepresentation =
      this.getDigitsFormat(formatOptions.withPrecision, amount.minimum) &&
      this.getDigitsFormat(formatOptions.withPrecision, amount.maximum);

    const parentMinimum = formatOptions.compact
      ? this.formatCompactAmount(amount.minimum, formatOptions)
      : currencyPipe.transform(
          amount.minimum,
          code,
          '',
          decimalRepresentation,
        ) || amount.minimum.toString();
    const parentMaximum =
      formatOptions.compact && amount.maximum
        ? this.formatCompactAmount(amount.maximum, formatOptions)
        : currencyPipe.transform(
            amount.maximum,
            code,
            '',
            decimalRepresentation,
          ) || (amount.maximum ? amount.maximum.toString() : '');

    return `${this.getSignForCurrency(code)}${parentMinimum}${
      parentMaximum ? ' – ' : '+'
    }${
      parentMaximum
        ? `${formatOptions.showSignBeforeMaximum ? this.getSignForCurrency(code) : ''}${parentMaximum}`
        : ''
    } ${formatOptions.disableCode ? '' : code}`;
  }

  displaySingleTimeAmount(amount: number): string {
    return amount === 1
      ? `${amount.toFixed(2)} hour`
      : `${amount.toFixed(2)} hours`;
  }

  displayRangeTimeAmount(amount: AmountRange): string | null {
    // minimum not defined, invalid
    if (!isDefined(amount.minimum)) {
      return null;
    }

    // maximum not defined, only display minimum
    if (!isDefined(amount.maximum)) {
      return `${amount.minimum.toFixed(2)}+ hours`;
    }

    // both displayed
    return `${amount.minimum.toFixed(2)} – ${amount.maximum.toFixed(2)} hours`;
  }

  getSignForCurrency(code: string): string {
    const sign = currencySignMap[code.toLowerCase()];
    if (!sign) {
      throw new Error(`Invalid currency code '${code}' in currency pipe.`);
    }
    return sign;
  }

  // TODO: T99032 get this logic from the sign map
  isTimeBasedCurrency(code: string): boolean {
    return code === 'TKN';
  }

  private getDigitsFormat(
    withPrecision?: boolean,
    amount?: number,
  ): string | undefined {
    if (withPrecision) {
      // Use the default fraction format in the angular currency pipe.
      return undefined;
    }
    return !amount || Number.isInteger(amount)
      ? this.NO_FRACTION_FORMAT
      : undefined;
  }

  /**
   * Transforms numbers to compact format with K, M, B suffixes
   * e.g., 1000 -> 1k, 1500 -> 1.5k, 1000000 -> 1M
   *
   * @param amount The number to format
   * @param formatOptions The formatting options to apply
   * @returns Formatted compact string representation
   */
  private formatCompactAmount(
    amount: number,
    formatOptions: CurrencyFormatOptions = {},
  ): string {
    // Handle numbers under 1000 without suffix
    if (Math.abs(amount) < 1000) {
      return amount.toString();
    }

    const units = ['', 'k', 'M', 'B', 'T'];
    const absAmount = Math.abs(amount);

    // Prevent index out of bounds for extremely large numbers
    // Currently we only support up to T (trillion)
    const unitIndex = Math.min(
      Math.floor(Math.log10(absAmount) / 3),
      units.length - 1,
    );

    const unitValue = absAmount / 1000 ** unitIndex;

    // Determine decimal places based on withPrecision setting
    // and if it's a whole number
    const isWholeNumber = unitValue === Math.floor(unitValue);
    const decimalPlaces =
      formatOptions.withPrecision === true || isWholeNumber ? 0 : 1;

    // Remove trailing zeros by parsing and re-converting to string
    let formattedNumber = unitValue.toFixed(decimalPlaces);
    if (decimalPlaces > 0 && formattedNumber.includes('.')) {
      formattedNumber = parseFloat(formattedNumber).toString();
    }

    // Apply negative sign if needed
    const sign = amount < 0 ? '-' : '';

    return `${sign}${formattedNumber}${units[unitIndex]}`;
  }
}
